/**
 * User Preferences Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Locale } from '@nord-living/shared';
import api from '@/services/api';

export interface UserPreferences {
  userId: string;
  themeMode?: 'light' | 'dark' | 'system';
  locale?: Locale;
  colorPrimary?: string;
  colorSecondary?: string;
  colorAccent?: string;
  colorSuccess?: string;
  colorWarning?: string;
  colorError?: string;
  sidebarCollapsed?: boolean;
  compactMode?: boolean;
  animationsEnabled?: boolean;
}

export interface UpdateUserPreferencesPayload {
  themeMode?: 'light' | 'dark' | 'system';
  locale?: Locale;
  colorPrimary?: string;
  colorSecondary?: string;
  colorAccent?: string;
  colorSuccess?: string;
  colorWarning?: string;
  colorError?: string;
  sidebarCollapsed?: boolean;
  compactMode?: boolean;
  animationsEnabled?: boolean;
}

const userPreferencesKeys = {
  all: ['users', 'me', 'preferences'] as const,
};

export function useUserPreferences(enabled = true) {
  return useQuery({
    queryKey: userPreferencesKeys.all,
    enabled,
    queryFn: async () => {
      const { data } = await api.get<{ data: UserPreferences | null }>('/users/me/preferences');
      return data.data;
    },
  });
}

export function useUpdateUserPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateUserPreferencesPayload) => {
      const { data } = await api.put<{ data: UserPreferences }>('/users/me/preferences', payload);
      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userPreferencesKeys.all });
    },
  });
}
